import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Grid2, Slider } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";

type Props = {
	startTime: Date;
	endTime: Date;
	onTimeChange: (startTime: Date) => void;
};

const formatTime = (date: Date) => {
	return date.toTimeString().split(" ")[0];
};

const CustomTimeSlider = ({ startTime, endTime, onTimeChange }: Props) => {
	const [sliderValue, setSliderValue] = useState<number>(50);
	const [startLabel, setStartLabel] = useState<string>("");
	const [endLabel, setEndLabel] = useState<string>("");
	const [totalRange, setTotalRange] = useState<number>(0);

	const theme = useMemo(
		() =>
			createTheme({
				components: {
					MuiSlider: {
						styleOverrides: {
							root: { height: 8 },
							thumb: {
								color: "#3f51b5",
								height: 20,
								width: 20,
								marginTop: -7,
							},
							track: {
								color: "#3f51b5",
								height: 8,
							},
							rail: {
								color: "green",
								height: 8,
							},
						},
					},
				},
			}),
		[],
	);

	const handleSliderChange = useCallback(
		(_event: Event, newValue: number | number[]): void => {
			const value = Array.isArray(newValue) ? newValue[0] : newValue;
			setSliderValue(value);

			const percentagePosition = value / 100;
			const newTimeValue =
				startTime.getTime() + totalRange * percentagePosition;
			const newTime = new Date(newTimeValue);
			onTimeChange(newTime);
		},
		[onTimeChange, startTime, totalRange],
	);

	const marks = useMemo(
		() => [
			{
				value: 0,
				label: startLabel,
			},
			{
				value: 100,
				label: endLabel,
			},
		],
		[startLabel, endLabel],
	);

	useEffect(() => {
		if (startTime && endTime) {
			setStartLabel(dxtToLocalServerTime(startTime, "HH:mm:ss"));
			setEndLabel(dxtToLocalServerTime(endTime, "HH:mm:ss"));
			setTotalRange(endTime.getTime() - startTime.getTime());

			setSliderValue(50);
		}
	}, []);

	return (
		<ThemeProvider theme={theme}>
			<Grid2 container spacing={2}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Slider
						value={sliderValue}
						onChange={handleSliderChange}
						step={1}
						marks={marks}
						min={0}
						max={100}
					/>
				</Grid2>
			</Grid2>
		</ThemeProvider>
	);
};

export default CustomTimeSlider;
export { formatTime };
